// ═══════════════════════════════════════════════════════════════════════════════
// ApplicantDetailsScreen.js — rich 6-tab candidate view for recruiters
// Fetches /api/recruitment/applicant/:id/full which returns a joined record of:
//   application + user + skills + education + experience + certifications +
//   languages + social_links + documents + status history + notes
// ═══════════════════════════════════════════════════════════════════════════════

// VERSION MARKER — logs on import. If this line never appears in Metro,
// the file on your device is NOT this version.
const __VERSION__ = 'ApplicantDetails v3-bulletproof 2026-05-18';
console.log(`[ApplicantDetails] module loaded: ${__VERSION__}`);

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView, SafeAreaView,
  StatusBar, Image, ActivityIndicator, FlatList, RefreshControl, Alert,
  Linking, Dimensions, Modal, TextInput, Share, Platform,
} from 'react-native';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import { Ionicons, MaterialCommunityIcons, Feather, FontAwesome5 } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';

let LinearGradient;
try { LinearGradient = require('expo-linear-gradient').LinearGradient; }
catch { LinearGradient = ({ colors, style, children, ...p }) =>
  <View style={[style, { backgroundColor: colors?.[0] || '#0e4e96' }]} {...p}>{children}</View>; }

const { width: SW } = Dimensions.get('window');

// ─── API ───────────────────────────────────────────────────────────────────
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const apiFetch = async (endpoint, options = {}) => {
  try {
    const token = await AsyncStorage.getItem('auth_token').catch(() => null);
    const res = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      return { error: true, status: res.status, message: text };
    }
    return await res.json();
  } catch (err) {
    return { error: true, message: err.message };
  }
};

// ─── Theme ─────────────────────────────────────────────────────────────────
const T = {
  primary: '#0e4e96',
  primaryLight: '#1a68c4',
  text: '#0f172a',
  textSec: '#475569',
  textMut: '#94a3b8',
  border: '#e2e8f0',
  bg: '#f8fafc',
  white: '#ffffff',
  success: '#059669',
  warn: '#d97706',
  danger: '#dc2626',
  info: '#0284c7',
  purple: '#7c3aed',
  pink: '#db2777',
};

const STAGE = {
  applicants:   { label: 'New',            color: T.info,    bg: '#dbeafe', icon: 'person-add-outline' },
  screening:    { label: 'Screening',      color: T.purple,  bg: '#ede9fe', icon: 'search-outline' },
  interview:    { label: 'Interview',      color: T.warn,    bg: '#fef3c7', icon: 'videocam-outline' },
  assessment:   { label: 'Assessment',     color: T.pink,    bg: '#fce7f3', icon: 'clipboard-outline' },
  offer:        { label: 'Offer',          color: T.success, bg: '#d1fae5', icon: 'gift-outline' },
  hired:        { label: 'Hired',          color: T.success, bg: '#a7f3d0', icon: 'trophy-outline' },
  rejected:     { label: 'Rejected',       color: T.danger,  bg: '#fee2e2', icon: 'close-circle-outline' },
  withdrawn:    { label: 'Withdrawn',      color: T.textMut, bg: '#f1f5f9', icon: 'exit-outline' },
};
const stageInfo = (s = 'applicants') => STAGE[(s || '').toLowerCase()] || STAGE.applicants;

const SKILL_LEVEL = { 1: 'Beginner', 2: 'Intermediate', 3: 'Advanced', 4: 'Expert', 5: 'Master' };

// ─── Helpers ───────────────────────────────────────────────────────────────
const fmtDate = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }); }
  catch { return String(d); }
};
const fmtDateTime = (d) => {
  if (!d) return '—';
  try { return new Date(d).toLocaleString('en-GB', { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }); }
  catch { return String(d); }
};
const yearsBetween = (start, end) => {
  if (!start) return '';
  const s = new Date(start);
  const e = end ? new Date(end) : new Date();
  const months = Math.max(0, (e.getFullYear() - s.getFullYear()) * 12 + (e.getMonth() - s.getMonth()));
  if (months < 12) return `${months} mo`;
  const y = Math.floor(months / 12), m = months % 12;
  return m ? `${y} yr ${m} mo` : `${y} yr`;
};
const initials = (name = '') =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
export default function ApplicantDetailsScreen() {
  const navigation = useNavigation();
  const route = useRoute();
  const applicantId = route.params?.applicantId || route.params?.id || route.params?.applicant?.id;

  const [loading, setLoading]       = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData]             = useState(null);
  const [error, setError]           = useState(null);
  const [activeTab, setActiveTab]   = useState('overview');
  const [showStageModal, setShowStageModal] = useState(false);
  const [showNoteModal, setShowNoteModal]   = useState(false);
  const [newNote, setNewNote]               = useState('');
  const [starred, setStarred]               = useState(false);

  // ── Load full applicant data ─────────────────────────────────────────────
  // Three parallel fetches against the real endpoints, merged into the shape
  // the rest of the screen reads: { application, notes, status_history,
  // interviews, offers }. Replaces the old /api/recruitment/applicant/:id/full
  // path (which never existed server-side).
  const loadApplicant = useCallback(async (isRefresh = false) => {
    if (!applicantId) { setError('No applicant ID provided'); setLoading(false); return; }
    if (!isRefresh) setLoading(true);
    try {
      const [appRes, notesRes, timelineRes] = await Promise.allSettled([
        apiFetch(`/api/recruitment/applicant/${applicantId}`),
        apiFetch(`/api/recruitment/applicant/${applicantId}/notes`),
        apiFetch(`/api/recruitment/applicant/${applicantId}/timeline`),
      ]);
      const app   = appRes.status === 'fulfilled' ? appRes.value : null;
      if (!app || app.error) throw new Error(app?.message || 'Failed to load applicant');

      const notes = (notesRes.status === 'fulfilled' && Array.isArray(notesRes.value)) ? notesRes.value : [];
      const timeline = (timelineRes.status === 'fulfilled' && Array.isArray(timelineRes.value)) ? timelineRes.value : [];

      // Reshape: the endpoint now returns one flat row with ALL the
      // applications columns, the JOINed users.profile JSONB lifted onto
      // profile_* fields, plus inline interviews/offers. We rebuild the
      // top-level data shape the screen reads.
      const { interviews = [], offers = [], ...applicationFields } = app;

      // Skills — already an array column on applications. DB returns plain
      // strings ("Node.js", "React Native"); SkillChip expects objects with
      // skill_name + skill_level. Coerce to the object shape so both layouts
      // (string-array from applications.skills OR a future user_skills join)
      // render correctly.
      const rawSkills = Array.isArray(applicationFields.skills)
        ? applicationFields.skills
        : (typeof applicationFields.skills === 'string'
            ? applicationFields.skills.split(',').map(s => s.trim()).filter(Boolean)
            : []);
      const skills = rawSkills.map((s, i) => {
        if (s && typeof s === 'object') {
          // Already in object shape (future user_skills JOIN or profile JSONB)
          return {
            id: s.id || `s_${i}`,
            skill_name: s.skill_name || s.name || '',
            skill_level: s.skill_level || s.level || null,
            is_primary: !!s.is_primary,
          };
        }
        // Plain string — wrap it
        return { id: `s_${i}`, skill_name: String(s), skill_level: null, is_primary: false };
      });

      // Experience — sub-table column names are job_title, company_name,
      // is_current, start_date, end_date, description. We map them to the
      // frontend's expected shape. Falls back to current_title synthesis
      // if the user has no entries in user_experience yet.
      let experience = [];
      const profExp = Array.isArray(applicationFields.profile_experience) ? applicationFields.profile_experience : [];
      if (profExp.length) {
        experience = profExp.map((e, i) => ({
          id: e.id || `exp_${i}`,
          // Sub-table uses job_title; JSONB shape uses title — accept both
          title:    e.job_title || e.title || e.position || e.role || '',
          // Sub-table uses company_name; JSONB shape uses company
          company:  e.company_name || e.company || e.employer || e.organisation || '',
          location: e.location || '',
          start_date: e.start_date || e.from || null,
          end_date:   e.end_date || e.to || null,
          current:    !!e.is_current || !!e.current || !e.end_date,
          duration:   e.duration
                    || (e.start_date && e.end_date ? `${String(e.start_date).slice(0,4)} – ${String(e.end_date).slice(0,4)}` : '')
                    || (e.is_current ? 'Present' : ''),
          description: e.description || e.summary || '',
          achievements: Array.isArray(e.achievements) ? e.achievements : [],
        }));
      } else if (applicationFields.current_title || applicationFields.current_company) {
        experience.push({
          id: `current_${applicationFields.id}`,
          title:    applicationFields.current_title || '—',
          company:  applicationFields.current_company || '',
          duration: applicationFields.experience_years
            ? `${applicationFields.experience_years} years`
            : '',
          current:  true,
          achievements: [],
        });
      }

      // Education — sub-table columns: institution, degree, field_of_study,
      // start_date, end_date, grade, description
      const profEdu = Array.isArray(applicationFields.profile_education) ? applicationFields.profile_education : [];
      const education = profEdu.map((e, i) => ({
        id: e.id || `edu_${i}`,
        degree:       e.degree || e.qualification || '',
        institution:  e.institution || e.school || e.university || '',
        // Sub-table uses field_of_study; JSONB shape uses field — accept both
        field:        e.field_of_study || e.field || e.subject || e.major || '',
        start_date:   e.start_date || e.from || null,
        end_date:     e.end_date || e.to || null,
        grade:        e.grade || e.gpa || '',
        description:  e.description || '',
      }));

      // Certifications — sub-table columns: cert_name, issuing_organization,
      // issue_date, expiry_date, credential_id, credential_url
      const profCerts = Array.isArray(applicationFields.profile_certifications) ? applicationFields.profile_certifications : [];
      const certifications = profCerts.map((c, i) => ({
        id: c.id || `cert_${i}`,
        cert_name:            c.cert_name || c.name || c.title || '',
        issuing_organization: c.issuing_organization || c.issuer || c.organisation || '',
        issue_date:           c.issue_date || c.date || null,
        expiry_date:          c.expiry_date || null,
        credential_id:        c.credential_id || '',
        credential_url:       c.credential_url || c.url || null,
      }));

      // Languages — sub-table columns: language_name, proficiency_level
      const profLangs = Array.isArray(applicationFields.profile_languages) ? applicationFields.profile_languages : [];
      const languages = profLangs.map((l, i) => {
        if (typeof l === 'string') return { id: `lang_${i}`, language_name: l, proficiency_level: '' };
        return {
          id: l.id || `lang_${i}`,
          language_name:      l.language_name || l.language || l.name || '',
          proficiency_level:  l.proficiency_level || l.level || l.proficiency || '',
        };
      });

      // Social links — combine LinkedIn from any source + github/twitter/portfolio
      const social_links = [];
      const pushSocial = (platform, url) => {
        if (url) social_links.push({
          id: `social_${platform}_${applicationFields.id}`,
          platform,
          profile_url: url,
        });
      };
      pushSocial('linkedin', applicationFields.candidate_linkedin || applicationFields.profile_linkedin_url || applicationFields.linkedin_url);
      pushSocial('github',   applicationFields.profile_github_url);
      pushSocial('twitter',  applicationFields.profile_twitter_url);
      pushSocial('website',  applicationFields.profile_portfolio_url);

      // Documents — resume from any source
      const documents = [];
      const resumeUrl = applicationFields.resume_url || applicationFields.profile_resume_url;
      if (resumeUrl) {
        documents.push({
          id: `resume_${applicationFields.id}`,
          document_name: 'Resume / CV',
          document_type: 'resume',
          file_url:      resumeUrl,
          mime_type:     'PDF',
        });
      }

      // Build user envelope. Use bio from profile JSONB if present.
      const user = {
        id:         applicationFields.candidate_user_id,
        full_name:  applicationFields.candidate_name,
        email:      applicationFields.candidate_email,
        phone:      applicationFields.candidate_phone || applicationFields.candidate_user_phone,
        avatar:     applicationFields.candidate_avatar,
        avatar_url: applicationFields.candidate_avatar,
        location:   applicationFields.candidate_location,
        bio:        applicationFields.profile_bio,
        job_title:  applicationFields.candidate_job_title,
        industry:   applicationFields.candidate_industry,
      };

      setData({
        application: applicationFields,
        user,
        skills,
        experience,
        education,
        certifications,
        languages,
        social_links,
        documents,
        notes,
        status_history: timeline,
        interviews,
        offers,
        match_score: applicationFields.match_score || null,
      });
      setStarred(!!applicationFields.is_starred);
      setError(null);
    } catch (e) {
      setError(e.message || 'Failed to load');
      console.warn('ApplicantDetails load error:', e?.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [applicantId]);

  useFocusEffect(useCallback(() => { loadApplicant(); }, [loadApplicant]));

  // ── Actions ──────────────────────────────────────────────────────────────
  const onRefresh = () => { setRefreshing(true); loadApplicant(true); };

  const toggleStar = async () => {
    const next = !starred;
    setStarred(next);
    await apiFetch(`/api/data/applications/${applicantId}`, {
      method: 'PUT',
      body: JSON.stringify({ is_starred: next }),
    }).catch(() => setStarred(!next));
  };

  const moveToStage = async (newStage) => {
    setShowStageModal(false);
    if (!data) return;
    const prevStage = data.application.stage;
    // Optimistic update
    setData(d => ({ ...d, application: { ...d.application, stage: newStage } }));
    // Use the recruitment stage endpoint — it writes the change AND records
    // the event in application_timeline so the history view stays accurate.
    const r = await apiFetch(`/api/recruitment/applicant/${applicantId}/stage`, {
      method: 'POST',
      body: JSON.stringify({ stage: newStage }),
    });
    if (r.error) {
      setData(d => ({ ...d, application: { ...d.application, stage: prevStage } }));
      Alert.alert('Update failed', r.message || 'Could not change stage');
    } else {
      // Reflect the timeline insert client-side (real one already happened server-side)
      setData(d => ({
        ...d,
        status_history: [
          { id: Date.now(), event_type: 'stage_change', detail: newStage, notes: `Moved to ${newStage}`, created_at: new Date().toISOString() },
          ...(d.status_history || []),
        ],
      }));
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    const noteText = newNote.trim();
    setNewNote('');
    setShowNoteModal(false);
    // Canonical notes endpoint — stores in the dedicated applicant_notes
    // table so multiple recruiters can leave threaded comments without
    // trampling each other's edits.
    const r = await apiFetch(`/api/recruitment/applicant/${applicantId}/notes`, {
      method: 'POST',
      body: JSON.stringify({ content: noteText }),
    });
    if (!r.error) {
      setData(d => ({
        ...d,
        notes: [{ id: r.id || Date.now(), content: noteText, created_at: r.created_at || new Date().toISOString() }, ...(d.notes || [])],
      }));
    } else {
      Alert.alert('Could not save note', r.message || 'Please try again.');
    }
  };

  const contactAction = (type, value) => {
    if (!value) return;
    if (type === 'email')    Linking.openURL(`mailto:${value}`);
    if (type === 'phone')    Linking.openURL(`tel:${value}`);
    if (type === 'website')  Linking.openURL(/^https?:\/\//.test(value) ? value : `https://${value}`);
  };

  const shareCandidate = async () => {
    if (!data) return;
    const a = data.application;
    try {
      await Share.share({
        message: `${a.candidate_name} — ${a.current_title || 'Candidate'}\n${a.candidate_email}\nApplying for: ${a.job_title_full || a.job_title}`,
      });
    } catch {}
  };

  // ─── RENDER ──────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <SafeAreaView style={st.safe}>
        <StatusBar barStyle="light-content" />
        <View style={st.centered}><ActivityIndicator size="large" color={T.primary} /></View>
      </SafeAreaView>
    );
  }

  if (error || !data) {
    return (
      <SafeAreaView style={st.safe}>
        <StatusBar barStyle="light-content" />
        <View style={st.centered}>
          <Ionicons name="alert-circle-outline" size={48} color={T.danger} />
          <Text style={st.errorTitle}>Could not load applicant</Text>
          <Text style={st.errorSub}>{error || 'No data'}</Text>
          <TouchableOpacity style={st.retryBtn} onPress={() => loadApplicant()}>
            <Text style={st.retryBtnText}>Retry</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  // Bulletproof every collection. Destructure defaults only fire on strict
  // `undefined` — `null` or any other non-array value still slips through and
  // crashes when we read .length downstream. Use a coercer to be explicit.
  const arr = v => Array.isArray(v) ? v : [];
  const app             = data?.application || {};
  const user            = data?.user || {};
  const skills          = arr(data?.skills);
  const education       = arr(data?.education);
  const experience      = arr(data?.experience);
  const certifications  = arr(data?.certifications);
  const languages       = arr(data?.languages);
  const social_links    = arr(data?.social_links);
  const documents       = arr(data?.documents);
  const status_history  = arr(data?.status_history);
  const notes           = arr(data?.notes);
  const interviews      = arr(data?.interviews);
  const offers          = arr(data?.offers);
  const match_score     = data?.match_score ?? null;

  const stage = stageInfo(app.stage);

  return (
    <SafeAreaView style={st.safe}>
      <StatusBar barStyle="light-content" />

      {/* ─── HEADER ─── */}
      <LinearGradient colors={[T.primary, T.primaryLight]} style={st.header}>
        <View style={st.headerRow}>
          <TouchableOpacity style={st.iconBtn} onPress={() => navigation.goBack()}>
            <Ionicons name="arrow-back" size={22} color={T.white} />
          </TouchableOpacity>
          <Text style={st.headerTitle} numberOfLines={1}>Candidate Details</Text>
          <View style={st.headerActions}>
            <TouchableOpacity style={st.iconBtn} onPress={toggleStar}>
              <Ionicons name={starred ? 'star' : 'star-outline'} size={20} color={starred ? '#fbbf24' : T.white} />
            </TouchableOpacity>
            <TouchableOpacity style={st.iconBtn} onPress={shareCandidate}>
              <Feather name="share-2" size={18} color={T.white} />
            </TouchableOpacity>
          </View>
        </View>
      </LinearGradient>

      <ScrollView
        contentContainerStyle={{ paddingBottom: 120 }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={T.primary} />}
      >
        {/* ─── CANDIDATE CARD ─── */}
        <View style={st.card}>
          <View style={st.heroRow}>
            {user?.avatar || user?.profile_image ? (
              <Image source={{ uri: user.avatar || user.profile_image }} style={st.avatar} />
            ) : (
              <View style={[st.avatar, st.avatarPh]}>
                <Text style={st.avatarInit}>{initials(app.candidate_name || user?.full_name)}</Text>
              </View>
            )}
            <View style={{ flex: 1, marginLeft: 14 }}>
              <Text style={st.name}>{app.candidate_name || user?.full_name || 'Unnamed'}</Text>
              <Text style={st.role}>{app.current_title || user?.profession || 'Candidate'}</Text>
              <View style={st.metaRow}>
                {app.candidate_location || user?.nationality ? (
                  <View style={st.metaItem}>
                    <Ionicons name="location-outline" size={12} color={T.textMut} />
                    <Text style={st.metaText}>{app.candidate_location || user?.nationality}</Text>
                  </View>
                ) : null}
                {app.experience_years || user?.experience_years ? (
                  <View style={st.metaItem}>
                    <Ionicons name="time-outline" size={12} color={T.textMut} />
                    <Text style={st.metaText}>{app.experience_years || user?.experience_years} yrs exp</Text>
                  </View>
                ) : null}
              </View>
            </View>
            {match_score > 0 && (
              <View style={st.matchCircle}>
                <Text style={st.matchNum}>{match_score}</Text>
                <Text style={st.matchLbl}>match</Text>
              </View>
            )}
          </View>

          {/* Stage + Applied date */}
          <View style={st.stageRow}>
            <TouchableOpacity
              style={[st.stagePill, { backgroundColor: stage.bg }]}
              onPress={() => setShowStageModal(true)}
            >
              <Ionicons name={stage.icon} size={14} color={stage.color} />
              <Text style={[st.stagePillText, { color: stage.color }]}>{stage.label}</Text>
              <Ionicons name="chevron-down" size={12} color={stage.color} />
            </TouchableOpacity>
            <Text style={st.appliedAt}>Applied {fmtDate(app.applied_date || app.created_at)}</Text>
          </View>

          {/* Job + Company */}
          <View style={st.jobRow}>
            {app.company_logo ? (
              <Image source={{ uri: app.company_logo }} style={st.jobLogo} />
            ) : (
              <View style={[st.jobLogo, { backgroundColor: T.primary, alignItems: 'center', justifyContent: 'center' }]}>
                <Text style={{ color: T.white, fontWeight: '700' }}>{initials(app.company_name)}</Text>
              </View>
            )}
            <View style={{ flex: 1, marginLeft: 10 }}>
              <Text style={st.jobTitleText} numberOfLines={1}>{app.job_title_full || app.job_title || 'Role'}</Text>
              <Text style={st.jobCompany}>{app.company_name || '—'}</Text>
            </View>
          </View>

          {/* Contact quick actions */}
          <View style={st.contactRow}>
            <TouchableOpacity style={st.contactBtn} onPress={() => contactAction('email', app.candidate_email)}>
              <Ionicons name="mail-outline" size={18} color={T.primary} />
              <Text style={st.contactBtnText}>Email</Text>
            </TouchableOpacity>
            <TouchableOpacity style={st.contactBtn} onPress={() => contactAction('phone', app.candidate_phone)}>
              <Ionicons name="call-outline" size={18} color={T.primary} />
              <Text style={st.contactBtnText}>Call</Text>
            </TouchableOpacity>
            <TouchableOpacity style={st.contactBtn} onPress={() => navigation.navigate('ContactCandidateScreen', { applicantId, applicant: app })}>
              <Ionicons name="chatbubble-ellipses-outline" size={18} color={T.primary} />
              <Text style={st.contactBtnText}>Message</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* ─── TABS ─── */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={st.tabsRow}
        >
          {[
            { key: 'overview',   label: 'Overview',      icon: 'person-outline' },
            { key: 'resume',     label: 'Resume',        icon: 'document-text-outline' },
            { key: 'cover',      label: 'Cover Letter',  icon: 'mail-open-outline' },
            { key: 'experience', label: `Experience (${experience.length})`, icon: 'briefcase-outline' },
            { key: 'education',  label: `Education (${education.length})`,   icon: 'school-outline' },
            { key: 'skills',     label: `Skills (${skills.length})`,         icon: 'construct-outline' },
            { key: 'interviews', label: `Interviews (${interviews.length})`, icon: 'videocam-outline' },
            { key: 'history',    label: 'History',       icon: 'time-outline' },
            { key: 'notes',      label: `Notes (${notes.length})`,           icon: 'create-outline' },
          ].map(t => (
            <TouchableOpacity
              key={t.key}
              onPress={() => setActiveTab(t.key)}
              style={[st.tab, activeTab === t.key && st.tabActive]}
            >
              <Ionicons name={t.icon} size={14} color={activeTab === t.key ? T.white : T.textSec} />
              <Text style={[st.tabText, activeTab === t.key && st.tabTextActive]}>{t.label}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>

        {/* ─── TAB BODIES ─── */}
        {activeTab === 'overview'   && <OverviewTab app={app} user={user} socialLinks={social_links} languages={languages} certifications={certifications} />}
        {activeTab === 'resume'     && <ResumeTab documents={documents} app={app} user={user} skills={skills} experience={experience} education={education} certifications={certifications} languages={languages} social_links={social_links} />}
        {activeTab === 'cover'      && <CoverLetterTab app={app} />}
        {activeTab === 'experience' && <ExperienceTab items={experience} />}
        {activeTab === 'education'  && <EducationTab items={education} />}
        {activeTab === 'skills'     && <SkillsTab skills={skills} />}
        {activeTab === 'interviews' && (
          <InterviewsTab
            items={interviews}
            applicant={app}
            applicantId={applicantId}
            onReschedule={(iv) => navigation.navigate('RescheduleInterviewScreen', {
              interview: iv, applicantId, applicant: app,
            })}
            onScheduleNew={() => navigation.navigate('ScheduleInterviewScreen', {
              applicantId, applicant: app,
            })}
            onLaunchVideo={(iv) => navigation.navigate('VideoScreen', {
              applicationId: applicantId,
              interviewId:   iv.id,
              interviewType: iv.interview_type || 'video',
              meetingUrl:    iv.meeting_link,
              candidate: {
                id:    app.id,
                name:  app.candidate_name,
                email: app.candidate_email,
                phone: app.candidate_phone,
                jobTitle: app.job_title,
              },
              interviewers: Array.isArray(iv.interviewers) ? iv.interviewers : [],
              agenda:       iv.instructions || '',
            })}
          />
        )}
        {activeTab === 'history'    && <HistoryTab items={status_history} />}
        {activeTab === 'notes'      && <NotesTab notes={notes} onAdd={() => setShowNoteModal(true)} />}
      </ScrollView>

      {/* ─── BOTTOM ACTION BAR ─── */}
      <View style={st.actionBar}>
        <TouchableOpacity style={[st.actBtn, { backgroundColor: T.danger }]} onPress={() => moveToStage('rejected')}>
          <Ionicons name="close-circle-outline" size={16} color={T.white} />
          <Text style={st.actBtnText}>Reject</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[st.actBtn, { backgroundColor: T.warn }]}
          onPress={() => navigation.navigate('ScheduleInterviewScreen', { applicantId, applicant: app })}
        >
          <Ionicons name="calendar-outline" size={16} color={T.white} />
          <Text style={st.actBtnText}>Interview</Text>
        </TouchableOpacity>
        <TouchableOpacity style={[st.actBtn, { backgroundColor: T.primary, flex: 1.4 }]} onPress={() => setShowStageModal(true)}>
          <Ionicons name="swap-vertical-outline" size={16} color={T.white} />
          <Text style={st.actBtnText}>Move stage</Text>
        </TouchableOpacity>
      </View>

      {/* ─── STAGE MODAL ─── */}
      <Modal visible={showStageModal} transparent animationType="slide" onRequestClose={() => setShowStageModal(false)}>
        <TouchableOpacity style={st.modalBg} activeOpacity={1} onPress={() => setShowStageModal(false)}>
          <View style={st.modalCard}>
            <Text style={st.modalTitle}>Move to stage</Text>
            {Object.entries(STAGE).map(([key, info]) => (
              <TouchableOpacity key={key} style={st.stageOption} onPress={() => moveToStage(key)}>
                <View style={[st.stageDot, { backgroundColor: info.color }]} />
                <Ionicons name={info.icon} size={16} color={info.color} />
                <Text style={st.stageOptText}>{info.label}</Text>
                {app.stage === key && <Ionicons name="checkmark" size={18} color={T.success} />}
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </Modal>

      {/* ─── NOTE MODAL ─── */}
      <Modal visible={showNoteModal} transparent animationType="slide" onRequestClose={() => setShowNoteModal(false)}>
        <TouchableOpacity style={st.modalBg} activeOpacity={1} onPress={() => setShowNoteModal(false)}>
          <TouchableOpacity activeOpacity={1} style={st.modalCard}>
            <Text style={st.modalTitle}>Add private note</Text>
            <TextInput
              value={newNote}
              onChangeText={setNewNote}
              placeholder="Observations, strengths, concerns…"
              placeholderTextColor={T.textMut}
              style={st.noteInput}
              multiline
              autoFocus
            />
            <View style={{ flexDirection: 'row', gap: 8, marginTop: 12 }}>
              <TouchableOpacity style={[st.actBtn, { flex: 1, backgroundColor: T.textMut }]} onPress={() => setShowNoteModal(false)}>
                <Text style={st.actBtnText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[st.actBtn, { flex: 1, backgroundColor: T.primary }]} onPress={addNote}>
                <Text style={st.actBtnText}>Save note</Text>
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </SafeAreaView>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════
const OverviewTab = ({ app, user, socialLinks, languages, certifications }) => (
  <View style={{ padding: 16, gap: 14 }}>
    <Section title="About" icon="information-circle-outline">
      <Text style={st.body}>
        {user?.bio || user?.about || app.cover_letter || 'No bio provided.'}
      </Text>
    </Section>

    <Section title="Contact" icon="call-outline">
      <Row label="Email"    value={app.candidate_email} />
      <Row label="Phone"    value={app.candidate_phone} />
      <Row label="Location" value={app.candidate_location} />
      {app.current_company && <Row label="Current Employer" value={app.current_company} />}
      {app.current_title   && <Row label="Current Title"    value={app.current_title} />}
    </Section>

    {socialLinks?.length > 0 && (
      <Section title="Online Profiles" icon="globe-outline">
        {socialLinks.map(s => (
          <TouchableOpacity
            key={s.id}
            style={st.socialRow}
            onPress={() => Linking.openURL(s.profile_url)}
          >
            <View style={[st.socialIcon, { backgroundColor: socialColor(s.platform) }]}>
              <Ionicons name={socialIcon(s.platform)} size={14} color={T.white} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={st.socialPlat}>{(s.platform || '').toUpperCase()}</Text>
              <Text style={st.socialUrl} numberOfLines={1}>{s.profile_url}</Text>
            </View>
            <Ionicons name="open-outline" size={16} color={T.primary} />
          </TouchableOpacity>
        ))}
      </Section>
    )}

    {certifications?.length > 0 && (
      <Section title={`Certifications (${certifications.length})`} icon="ribbon-outline">
        {certifications.map(c => (
          <View key={c.id} style={st.certRow}>
            <MaterialCommunityIcons name="certificate-outline" size={20} color={T.warn} />
            <View style={{ flex: 1, marginLeft: 10 }}>
              <Text style={st.itemTitle}>{c.cert_name}</Text>
              <Text style={st.itemSub}>{c.issuing_organization}{c.issue_date ? ` · ${fmtDate(c.issue_date)}` : ''}</Text>
              {c.credential_url ? (
                <TouchableOpacity onPress={() => Linking.openURL(c.credential_url)}>
                  <Text style={st.linkText}>View credential</Text>
                </TouchableOpacity>
              ) : null}
            </View>
          </View>
        ))}
      </Section>
    )}

    {languages?.length > 0 && (
      <Section title={`Languages (${languages.length})`} icon="language-outline">
        <View style={st.chipWrap}>
          {languages.map(l => (
            <View key={l.id} style={st.chip}>
              <Text style={st.chipText}>{l.language_name}</Text>
              <Text style={st.chipSub}>· {l.proficiency_level}</Text>
            </View>
          ))}
        </View>
      </Section>
    )}

    <Section title="Application" icon="document-text-outline">
      <Row label="Applied"  value={fmtDateTime(app.applied_date || app.created_at)} />
      <Row label="Source"   value={app.source || 'mobile_app'} />
      <Row label="Status"   value={app.status} />
      <Row label="Stage"    value={stageInfo(app.stage).label} />
      {app.ai_score || app.overall_score ? (
        <Row label="AI score" value={String(app.ai_score || app.overall_score)} />
      ) : null}
    </Section>
  </View>
);

// Resume tab — renders an in-place CV from the candidate's data. In JobEng
// the resume IS the profile (no separate PDF), so we lay it out professionally
// rather than try to download an empty file. Mirrors how ResumePreviewScreen
// presents the candidate's own resume.
const ResumeTab = ({ documents, app, user, skills, experience, education,
                     certifications, languages, social_links }) => {
  // If a real document URL exists (rare — only if profile.resume_url is a
  // genuine uploaded PDF), give the recruiter a download button at the top.
  const externalDoc = (documents || []).find(d =>
    d.file_url && /^https?:\/\//.test(d.file_url) && !/example\.com/i.test(d.file_url)
  );

  const fullName  = app.candidate_name || user?.full_name || 'Candidate';
  const title     = app.current_title || user?.job_title || '';
  const email     = app.candidate_email || user?.email || '';
  const phone     = app.candidate_phone || user?.phone || '';
  const location  = app.candidate_location || user?.location || '';
  const bio       = user?.bio || '';
  const linkedin  = (social_links || []).find(s => s.platform === 'linkedin')?.profile_url;
  const github    = (social_links || []).find(s => s.platform === 'github')?.profile_url;
  const website   = (social_links || []).find(s => s.platform === 'website')?.profile_url;

  const hasAnyContent =
    bio || (experience || []).length || (education || []).length ||
    (skills || []).length || (certifications || []).length ||
    (languages || []).length || app.cover_letter;

  if (!hasAnyContent && !externalDoc) {
    return (
      <View style={{ padding: 16 }}>
        <EmptyBlock icon="document-outline" label="No resume content yet — the candidate hasn't filled in their profile" />
      </View>
    );
  }

  return (
    <View style={{ padding: 16, gap: 14 }}>
      {/* External PDF (only if there's a real one) */}
      {externalDoc ? (
        <View style={st.card}>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 10 }}>
            <FontAwesome5 name="file-pdf" size={26} color={T.danger} />
            <View style={{ flex: 1, marginLeft: 12 }}>
              <Text style={st.itemTitle}>{externalDoc.document_name || 'Resume'}</Text>
              <Text style={st.itemSub} numberOfLines={1}>{externalDoc.file_url}</Text>
            </View>
          </View>
          <View style={{ flexDirection: 'row', gap: 8 }}>
            <TouchableOpacity
              style={[st.actBtn, { flex: 1, backgroundColor: T.primary }]}
              onPress={() => Linking.openURL(externalDoc.file_url)}
            >
              <Ionicons name="open-outline" size={16} color={T.white} />
              <Text style={st.actBtnText}>Open PDF</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[st.actBtn, { flex: 1, backgroundColor: T.success }]}
              onPress={() => Share.share({ message: externalDoc.file_url, url: externalDoc.file_url })}
            >
              <Ionicons name="share-outline" size={16} color={T.white} />
              <Text style={st.actBtnText}>Share</Text>
            </TouchableOpacity>
          </View>
        </View>
      ) : null}

      {/* CV header */}
      <View style={[st.card, { paddingVertical: 18 }]}>
        <Text style={{ fontSize: 22, fontWeight: '800', color: T.textPri, textAlign: 'center' }}>
          {fullName}
        </Text>
        {title ? (
          <Text style={{ fontSize: 14, color: T.primary, textAlign: 'center', marginTop: 4, fontWeight: '600' }}>
            {title}
          </Text>
        ) : null}
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 10, marginTop: 8 }}>
          {email    ? <ContactPip icon="mail-outline"     text={email} /> : null}
          {phone    ? <ContactPip icon="call-outline"     text={phone} /> : null}
          {location ? <ContactPip icon="location-outline" text={location} /> : null}
          {linkedin ? <ContactPip icon="logo-linkedin"    text="LinkedIn" onPress={() => Linking.openURL(linkedin)} /> : null}
          {github   ? <ContactPip icon="logo-github"      text="GitHub"   onPress={() => Linking.openURL(github)} /> : null}
          {website  ? <ContactPip icon="globe-outline"    text="Website"  onPress={() => Linking.openURL(website)} /> : null}
        </View>
      </View>

      {/* Profile summary */}
      {bio ? (
        <View style={st.card}>
          <Text style={st.resumeSectionTitle}>PROFESSIONAL SUMMARY</Text>
          <Text style={[st.body, { lineHeight: 20 }]}>{bio}</Text>
        </View>
      ) : null}

      {/* Cover letter (the per-application narrative, often distinct from bio) */}
      {app.cover_letter ? (
        <View style={st.card}>
          <Text style={st.resumeSectionTitle}>COVER LETTER</Text>
          <Text style={[st.body, { lineHeight: 20 }]}>{app.cover_letter}</Text>
        </View>
      ) : null}

      {/* Experience */}
      {(experience || []).length > 0 ? (
        <View style={st.card}>
          <Text style={st.resumeSectionTitle}>EXPERIENCE</Text>
          {experience.map((e, i) => (
            <View key={e.id || i} style={{ marginTop: i > 0 ? 14 : 0 }}>
              <Text style={st.itemTitle}>{e.title}</Text>
              <Text style={[st.itemSub, { color: T.primary, fontWeight: '600' }]}>
                {e.company}{e.location ? ` · ${e.location}` : ''}
              </Text>
              <Text style={st.itemDate}>
                {e.start_date ? fmtDate(e.start_date) : ''}
                {e.start_date ? ' – ' : ''}
                {e.current ? 'Present' : (e.end_date ? fmtDate(e.end_date) : '')}
                {e.duration && !e.start_date ? e.duration : ''}
              </Text>
              {e.description ? (
                <Text style={[st.body, { marginTop: 6, lineHeight: 19 }]}>{e.description}</Text>
              ) : null}
              {Array.isArray(e.achievements) && e.achievements.length > 0 ? (
                <View style={{ marginTop: 6 }}>
                  {e.achievements.map((a, ai) => (
                    <Text key={ai} style={[st.body, { lineHeight: 19 }]}>• {a}</Text>
                  ))}
                </View>
              ) : null}
            </View>
          ))}
        </View>
      ) : null}

      {/* Education */}
      {(education || []).length > 0 ? (
        <View style={st.card}>
          <Text style={st.resumeSectionTitle}>EDUCATION</Text>
          {education.map((e, i) => (
            <View key={e.id || i} style={{ marginTop: i > 0 ? 12 : 0 }}>
              <Text style={st.itemTitle}>{e.degree}{e.field ? ` · ${e.field}` : ''}</Text>
              <Text style={[st.itemSub, { color: T.primary, fontWeight: '600' }]}>{e.institution}</Text>
              <Text style={st.itemDate}>
                {e.start_date ? fmtDate(e.start_date) : ''}
                {e.end_date ? (e.start_date ? ' – ' : '') + fmtDate(e.end_date) : ''}
                {e.grade ? ` · ${e.grade}` : ''}
              </Text>
              {e.description ? (
                <Text style={[st.body, { marginTop: 4 }]}>{e.description}</Text>
              ) : null}
            </View>
          ))}
        </View>
      ) : null}

      {/* Skills */}
      {(skills || []).length > 0 ? (
        <View style={st.card}>
          <Text style={st.resumeSectionTitle}>SKILLS</Text>
          <View style={st.chipWrap}>
            {skills.map(s => (
              <View key={s.id} style={[st.chip, { backgroundColor: T.primary + '12' }]}>
                <Text style={[st.chipText, { color: T.primary }]}>{s.skill_name}</Text>
              </View>
            ))}
          </View>
        </View>
      ) : null}

      {/* Certifications */}
      {(certifications || []).length > 0 ? (
        <View style={st.card}>
          <Text style={st.resumeSectionTitle}>CERTIFICATIONS</Text>
          {certifications.map((c, i) => (
            <View key={c.id || i} style={{ marginTop: i > 0 ? 10 : 0, flexDirection: 'row' }}>
              <MaterialCommunityIcons name="certificate-outline" size={18} color={T.warn} style={{ marginTop: 2 }} />
              <View style={{ flex: 1, marginLeft: 10 }}>
                <Text style={st.itemTitle}>{c.cert_name}</Text>
                <Text style={st.itemSub}>
                  {c.issuing_organization}{c.issue_date ? ` · ${fmtDate(c.issue_date)}` : ''}
                </Text>
                {c.credential_url ? (
                  <TouchableOpacity onPress={() => Linking.openURL(c.credential_url)}>
                    <Text style={st.linkText}>View credential</Text>
                  </TouchableOpacity>
                ) : null}
              </View>
            </View>
          ))}
        </View>
      ) : null}

      {/* Languages */}
      {(languages || []).length > 0 ? (
        <View style={st.card}>
          <Text style={st.resumeSectionTitle}>LANGUAGES</Text>
          <View style={st.chipWrap}>
            {languages.map(l => (
              <View key={l.id} style={st.chip}>
                <Text style={st.chipText}>{l.language_name}</Text>
                {l.proficiency_level ? (
                  <Text style={st.chipSub}>· {l.proficiency_level}</Text>
                ) : null}
              </View>
            ))}
          </View>
        </View>
      ) : null}

      {/* Footer action — share full resume content with team */}
      <TouchableOpacity
        style={[st.actBtn, { backgroundColor: T.primary, marginTop: 4 }]}
        onPress={() => Share.share({
          message: `${fullName}\n${title}\n${email}${phone ? ` · ${phone}` : ''}${linkedin ? `\nLinkedIn: ${linkedin}` : ''}\n\n${bio || ''}`,
        })}
      >
        <Ionicons name="share-outline" size={16} color={T.white} />
        <Text style={st.actBtnText}>Share resume summary</Text>
      </TouchableOpacity>
    </View>
  );
};

// Small helper for the contact strip at the top of the resume card
const ContactPip = ({ icon, text, onPress }) => {
  const Wrap = onPress ? TouchableOpacity : View;
  return (
    <Wrap onPress={onPress} style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
      <Ionicons name={icon} size={12} color={T.textSec} />
      <Text style={{ fontSize: 12, color: onPress ? T.primary : T.textSec }}>{text}</Text>
    </Wrap>
  );
};

const CoverLetterTab = ({ app }) => (
  <View style={{ padding: 16 }}>
    {app.cover_letter ? (
      <View style={st.card}>
        <Text style={st.body}>{app.cover_letter}</Text>
      </View>
    ) : (
      <EmptyBlock icon="mail-open-outline" label="No cover letter submitted" />
    )}
  </View>
);

const ExperienceTab = ({ items }) => (
  <View style={{ padding: 16, gap: 10 }}>
    {items.length === 0 && <EmptyBlock icon="briefcase-outline" label="No work experience on file" />}
    {items.map((e, idx) => (
      <View key={e.id || idx} style={st.card}>
        <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
          <View style={st.timelineDot}><Ionicons name="briefcase" size={14} color={T.white} /></View>
          <View style={{ flex: 1, marginLeft: 12 }}>
            <Text style={st.itemTitle}>{e.job_title}</Text>
            <Text style={st.itemSub}>{e.company_name}{e.location ? ` · ${e.location}` : ''}</Text>
            <Text style={st.itemDate}>
              {fmtDate(e.start_date)} — {e.is_current ? 'Present' : fmtDate(e.end_date)}
              {'  · '}{yearsBetween(e.start_date, e.end_date)}
            </Text>
            {e.description ? <Text style={[st.body, { marginTop: 8 }]}>{e.description}</Text> : null}
            {Array.isArray(e.achievements) && e.achievements.length > 0 && (
              <View style={{ marginTop: 8 }}>
                {e.achievements.map((ach, i) => (
                  <View key={i} style={st.bulletRow}>
                    <Text style={st.bullet}>•</Text>
                    <Text style={[st.body, { flex: 1 }]}>{ach}</Text>
                  </View>
                ))}
              </View>
            )}
          </View>
        </View>
      </View>
    ))}
  </View>
);

const EducationTab = ({ items }) => (
  <View style={{ padding: 16, gap: 10 }}>
    {items.length === 0 && <EmptyBlock icon="school-outline" label="No education on file" />}
    {items.map((e, idx) => (
      <View key={e.id || idx} style={st.card}>
        <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
          <View style={[st.timelineDot, { backgroundColor: T.purple }]}><Ionicons name="school" size={14} color={T.white} /></View>
          <View style={{ flex: 1, marginLeft: 12 }}>
            <Text style={st.itemTitle}>{e.degree}{e.field_of_study ? ` · ${e.field_of_study}` : ''}</Text>
            <Text style={st.itemSub}>{e.institution}</Text>
            <Text style={st.itemDate}>
              {fmtDate(e.start_date)} — {fmtDate(e.end_date)}
              {e.grade ? `  · ${e.grade}` : ''}
            </Text>
            {e.description ? <Text style={[st.body, { marginTop: 6 }]}>{e.description}</Text> : null}
          </View>
        </View>
      </View>
    ))}
  </View>
);

const SkillsTab = ({ skills }) => {
  const primary = skills.filter(s => s.is_primary);
  const other   = skills.filter(s => !s.is_primary);
  return (
    <View style={{ padding: 16, gap: 14 }}>
      {skills.length === 0 && <EmptyBlock icon="construct-outline" label="No skills on file" />}
      {primary.length > 0 && (
        <Section title="Core skills" icon="star-outline">
          <View style={st.chipWrap}>
            {primary.map(s => <SkillChip key={s.id} skill={s} primary />)}
          </View>
        </Section>
      )}
      {other.length > 0 && (
        <Section title="Other skills" icon="construct-outline">
          <View style={st.chipWrap}>
            {other.map(s => <SkillChip key={s.id} skill={s} />)}
          </View>
        </Section>
      )}
    </View>
  );
};

const SkillChip = ({ skill, primary }) => (
  <View style={[st.skillChip, primary && st.skillChipPrimary]}>
    <Text style={[st.skillChipText, primary && { color: T.white }]}>{skill.skill_name}</Text>
    <Text style={[st.skillChipSub, primary && { color: '#e0e7ff' }]}>
      {SKILL_LEVEL[skill.skill_level] || '—'}
      {skill.years_experience ? ` · ${skill.years_experience}y` : ''}
    </Text>
  </View>
);

// Renders a candidate's scheduled/completed interviews with per-row actions.
// Reschedule, Join (when there's a meeting link), and Schedule New get
// wired to navigation by the parent so the same applicant context threads
// through. Status colours match the rest of the app.
const InterviewsTab = ({ items, applicant, applicantId, onReschedule, onScheduleNew, onLaunchVideo }) => {
  const STATUS_COLORS = {
    scheduled:   '#3B82F6',
    in_progress: '#F59E0B',
    completed:   '#10B981',
    cancelled:   '#EF4444',
    rescheduled: '#8B5CF6',
    no_show:     '#9CA3AF',
  };
  const openLink = (url) => {
    if (!url) return;
    Linking.openURL(url).catch(() => {});
  };
  return (
    <View style={{ padding: 16, gap: 10 }}>
      <TouchableOpacity
        style={[st.actBtn, { backgroundColor: T.primary, alignSelf: 'flex-start' }]}
        onPress={onScheduleNew}
      >
        <Ionicons name="calendar-outline" size={16} color={T.white} />
        <Text style={st.actBtnText}>Schedule new interview</Text>
      </TouchableOpacity>

      {items.length === 0 && <EmptyBlock icon="videocam-outline" label="No interviews scheduled yet" />}

      {items.map((iv) => {
        const status = (iv.status || 'scheduled').toLowerCase();
        const color  = STATUS_COLORS[status] || '#6B7280';
        const when   = iv.scheduled_date || iv.scheduled_at;
        const canReschedule = status === 'scheduled' || status === 'rescheduled' || status === 'in_progress';
        return (
          <View key={iv.id} style={[st.card, { padding: 14 }]}>
            <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
              <View style={{ flex: 1 }}>
                <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap' }}>
                  <Text style={[st.itemTitle, { textTransform: 'capitalize' }]}>
                    {iv.interview_type || 'Interview'}
                  </Text>
                  {iv.round_number ? (
                    <Text style={[st.itemDate, { marginLeft: 6 }]}>· Round {iv.round_number}</Text>
                  ) : null}
                  <View style={[
                    st.stagePill,
                    { backgroundColor: color + '20', marginLeft: 8 },
                  ]}>
                    <Text style={[st.stagePillText, { color }]}>{status}</Text>
                  </View>
                </View>
                <Text style={[st.body, { marginTop: 4 }]}>
                  {when ? fmtDateTime(when) : 'No time set'}
                  {iv.duration_minutes ? ` · ${iv.duration_minutes} min` : ''}
                </Text>
                {iv.interviewer_name ? (
                  <Text style={st.itemDate}>Interviewer: {iv.interviewer_name}</Text>
                ) : null}
                {iv.location ? (
                  <Text style={st.itemDate}>Location: {iv.location}</Text>
                ) : null}
                {/* Recruiter's notes on the interview, if any */}
                {iv.notes || iv.feedback ? (
                  <Text style={[st.body, { marginTop: 6, fontStyle: 'italic' }]} numberOfLines={3}>
                    {iv.notes || iv.feedback}
                  </Text>
                ) : null}
                {/* Show candidate-side feedback when present — both sides write the same row */}
                {iv.candidate_joined_at ? (
                  <Text style={[st.itemDate, { color: '#10B981' }]}>
                    ✓ Candidate joined {fmtDateTime(iv.candidate_joined_at)}
                  </Text>
                ) : null}
              </View>
            </View>

            <View style={{ flexDirection: 'row', gap: 8, marginTop: 12 }}>
              {iv.meeting_link ? (
                <TouchableOpacity
                  style={[st.actBtn, { backgroundColor: T.primary, flex: 1 }]}
                  onPress={() => onLaunchVideo(iv)}
                >
                  <Ionicons name="videocam" size={14} color={T.white} />
                  <Text style={st.actBtnText}>Join</Text>
                </TouchableOpacity>
              ) : null}
              {canReschedule ? (
                <TouchableOpacity
                  style={[st.actBtn, { backgroundColor: T.warn, flex: 1 }]}
                  onPress={() => onReschedule(iv)}
                >
                  <Ionicons name="time-outline" size={14} color={T.white} />
                  <Text style={st.actBtnText}>Reschedule</Text>
                </TouchableOpacity>
              ) : null}
            </View>
          </View>
        );
      })}
    </View>
  );
};

const HistoryTab = ({ items }) => (
  <View style={{ padding: 16, gap: 8 }}>
    {items.length === 0 && <EmptyBlock icon="time-outline" label="No status changes recorded" />}
    {items.map((h, i) => (
      <View key={h.id || i} style={st.timelineRow}>
        <View style={st.timelineLine}>
          <View style={[st.timelineDotSm, { backgroundColor: stageInfo(h.stage).color }]} />
          {i < items.length - 1 && <View style={st.timelineStick} />}
        </View>
        <View style={{ flex: 1, paddingBottom: 14 }}>
          <Text style={st.itemTitle}>{stageInfo(h.stage).label}{h.status ? ` · ${h.status}` : ''}</Text>
          {h.note ? <Text style={st.body}>{h.note}</Text> : null}
          <Text style={st.itemDate}>{fmtDateTime(h.created_at)}</Text>
        </View>
      </View>
    ))}
  </View>
);

const NotesTab = ({ notes, onAdd }) => (
  <View style={{ padding: 16, gap: 10 }}>
    <TouchableOpacity style={[st.actBtn, { backgroundColor: T.primary }]} onPress={onAdd}>
      <Ionicons name="add-circle-outline" size={16} color={T.white} />
      <Text style={st.actBtnText}>Add note</Text>
    </TouchableOpacity>
    {notes.length === 0 && <EmptyBlock icon="create-outline" label="No notes yet" />}
    {notes.map((n, i) => (
      <View key={n.id || i} style={st.card}>
        <Text style={st.body}>{n.note}</Text>
        <Text style={[st.itemDate, { marginTop: 6 }]}>{fmtDateTime(n.created_at)}</Text>
      </View>
    ))}
  </View>
);

// ═══════════════════════════════════════════════════════════════════════════
// SMALL COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════
const Section = ({ title, icon, children }) => (
  <View style={st.card}>
    <View style={st.sectionHeader}>
      <Ionicons name={icon} size={16} color={T.primary} />
      <Text style={st.sectionTitle}>{title}</Text>
    </View>
    {children}
  </View>
);

const Row = ({ label, value }) => (
  value ? (
    <View style={st.rowLine}>
      <Text style={st.rowLabel}>{label}</Text>
      <Text style={st.rowValue} numberOfLines={2}>{value}</Text>
    </View>
  ) : null
);

const EmptyBlock = ({ icon, label }) => (
  <View style={st.empty}>
    <Ionicons name={icon} size={28} color={T.textMut} />
    <Text style={st.emptyText}>{label}</Text>
  </View>
);

const socialIcon = (p) => ({
  linkedin: 'logo-linkedin', github: 'logo-github', twitter: 'logo-twitter',
  facebook: 'logo-facebook', instagram: 'logo-instagram', portfolio: 'globe-outline',
  website: 'globe-outline',
}[String(p || '').toLowerCase()] || 'link-outline');
const socialColor = (p) => ({
  linkedin: '#0077b5', github: '#181717', twitter: '#1da1f2',
  facebook: '#1877f2', instagram: '#e4405f', portfolio: T.primary, website: T.primary,
}[String(p || '').toLowerCase()] || T.textSec);

// ═══════════════════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════════════════
const st = StyleSheet.create({
  safe:     { flex: 1, backgroundColor: T.bg },
  centered: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
  errorTitle: { fontSize: 17, fontWeight: '700', color: T.text, marginTop: 10 },
  errorSub:   { fontSize: 13, color: T.textSec, marginTop: 4, textAlign: 'center' },
  retryBtn:   { marginTop: 16, backgroundColor: T.primary, paddingHorizontal: 20, paddingVertical: 10, borderRadius: 8 },
  retryBtnText: { color: T.white, fontWeight: '700' },

  header:        { paddingTop: Platform.OS === 'ios' ? 8 : 14, paddingBottom: 14, paddingHorizontal: 12 },
  headerRow:     { flexDirection: 'row', alignItems: 'center' },
  headerTitle:   { flex: 1, color: T.white, fontSize: 17, fontWeight: '700', marginLeft: 8 },
  headerActions: { flexDirection: 'row', gap: 4 },
  iconBtn:       { width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center' },

  card: {
    backgroundColor: T.white, borderRadius: 14, padding: 14, marginHorizontal: 12, marginTop: 12,
    borderWidth: 1, borderColor: T.border,
    shadowColor: '#000', shadowOpacity: 0.03, shadowOffset: { width: 0, height: 2 }, shadowRadius: 6, elevation: 1,
  },

  heroRow: { flexDirection: 'row', alignItems: 'center' },
  avatar:  { width: 64, height: 64, borderRadius: 32 },
  avatarPh:{ backgroundColor: T.primary, alignItems: 'center', justifyContent: 'center' },
  avatarInit: { color: T.white, fontWeight: '800', fontSize: 22 },
  name:    { fontSize: 18, fontWeight: '700', color: T.text },
  role:    { fontSize: 13, color: T.textSec, marginTop: 2 },
  metaRow: { flexDirection: 'row', marginTop: 6, gap: 10, flexWrap: 'wrap' },
  metaItem:{ flexDirection: 'row', alignItems: 'center', gap: 3 },
  metaText:{ fontSize: 11, color: T.textMut },

  matchCircle: { width: 54, height: 54, borderRadius: 27, backgroundColor: T.success,
    alignItems: 'center', justifyContent: 'center' },
  matchNum:    { color: T.white, fontSize: 18, fontWeight: '800' },
  matchLbl:    { color: T.white, fontSize: 8, fontWeight: '600', textTransform: 'uppercase' },

  stageRow: { flexDirection: 'row', alignItems: 'center', marginTop: 12, justifyContent: 'space-between' },
  stagePill: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 10, paddingVertical: 5, borderRadius: 14 },
  stagePillText: { fontWeight: '700', fontSize: 12 },
  appliedAt: { fontSize: 11, color: T.textMut },

  jobRow:      { flexDirection: 'row', alignItems: 'center', marginTop: 12, paddingTop: 12, borderTopWidth: 1, borderTopColor: T.border },
  jobLogo:     { width: 36, height: 36, borderRadius: 8 },
  jobTitleText:{ fontSize: 14, fontWeight: '700', color: T.text },
  jobCompany:  { fontSize: 12, color: T.textSec, marginTop: 2 },

  contactRow:  { flexDirection: 'row', gap: 8, marginTop: 12 },
  contactBtn:  { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
    paddingVertical: 10, borderRadius: 10, borderWidth: 1, borderColor: T.border, backgroundColor: T.bg },
  contactBtnText: { fontSize: 12, fontWeight: '700', color: T.primary },

  tabsRow: { paddingHorizontal: 12, paddingVertical: 10, gap: 8 },
  tab: { flexDirection: 'row', alignItems: 'center', gap: 5, paddingHorizontal: 12, paddingVertical: 8,
    borderRadius: 18, backgroundColor: T.white, borderWidth: 1, borderColor: T.border },
  tabActive: { backgroundColor: T.primary, borderColor: T.primary },
  tabText: { fontSize: 12, fontWeight: '600', color: T.textSec },
  tabTextActive: { color: T.white },

  sectionHeader:  { flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 10 },
  sectionTitle:   { fontSize: 13, fontWeight: '700', color: T.text, textTransform: 'uppercase', letterSpacing: 0.3 },
  subSectionTitle:{ fontSize: 12, fontWeight: '700', color: T.textSec, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.3 },
  resumeSectionTitle:{ fontSize: 11, fontWeight: '800', color: T.primary, marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1.2, borderBottomWidth: 2, borderBottomColor: T.primary + '30', paddingBottom: 6 },

  body: { fontSize: 13, color: T.text, lineHeight: 20 },
  itemTitle: { fontSize: 14, fontWeight: '700', color: T.text },
  itemSub:   { fontSize: 12, color: T.textSec, marginTop: 2 },
  itemDate:  { fontSize: 11, color: T.textMut, marginTop: 4 },
  linkText:  { fontSize: 12, color: T.primary, fontWeight: '600', marginTop: 4 },

  rowLine:   { flexDirection: 'row', paddingVertical: 6 },
  rowLabel:  { width: 120, fontSize: 12, color: T.textMut },
  rowValue:  { flex: 1, fontSize: 13, color: T.text, fontWeight: '500' },

  socialRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8, gap: 10 },
  socialIcon:{ width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center' },
  socialPlat:{ fontSize: 11, fontWeight: '700', color: T.text, letterSpacing: 0.3 },
  socialUrl: { fontSize: 11, color: T.textMut, marginTop: 1 },

  certRow:   { flexDirection: 'row', alignItems: 'flex-start', paddingVertical: 8 },

  chipWrap:  { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  chip:      { flexDirection: 'row', backgroundColor: T.bg, paddingHorizontal: 10, paddingVertical: 6, borderRadius: 14, borderWidth: 1, borderColor: T.border },
  chipText:  { fontSize: 12, color: T.text, fontWeight: '600' },
  chipSub:   { fontSize: 11, color: T.textMut, marginLeft: 4 },

  skillChip: { backgroundColor: T.bg, paddingHorizontal: 10, paddingVertical: 8, borderRadius: 10, borderWidth: 1, borderColor: T.border },
  skillChipPrimary: { backgroundColor: T.primary, borderColor: T.primary },
  skillChipText: { fontSize: 12, fontWeight: '700', color: T.text },
  skillChipSub:  { fontSize: 10, color: T.textMut, marginTop: 2 },

  timelineDot:   { width: 28, height: 28, borderRadius: 14, backgroundColor: T.primary, alignItems: 'center', justifyContent: 'center' },
  timelineDotSm: { width: 10, height: 10, borderRadius: 5 },
  timelineLine:  { alignItems: 'center', marginRight: 12, width: 10 },
  timelineStick: { width: 2, flex: 1, backgroundColor: T.border, marginTop: 2 },
  timelineRow:   { flexDirection: 'row' },

  bulletRow: { flexDirection: 'row', marginTop: 4 },
  bullet:    { fontSize: 14, color: T.primary, marginRight: 6, fontWeight: '700' },

  docRow:    { flexDirection: 'row', alignItems: 'center', paddingVertical: 6 },

  empty:     { alignItems: 'center', paddingVertical: 30, gap: 8 },
  emptyText: { fontSize: 12, color: T.textMut },

  actionBar: { position: 'absolute', left: 0, right: 0, bottom: 0, flexDirection: 'row', gap: 8,
    paddingHorizontal: 12, paddingVertical: 10, paddingBottom: Platform.OS === 'ios' ? 26 : 12,
    backgroundColor: T.white, borderTopWidth: 1, borderTopColor: T.border },
  actBtn:    { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6,
    paddingVertical: 12, borderRadius: 10 },
  actBtnText:{ color: T.white, fontSize: 13, fontWeight: '700' },

  modalBg:   { flex: 1, backgroundColor: 'rgba(15,23,42,0.5)', justifyContent: 'flex-end' },
  modalCard: { backgroundColor: T.white, borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 20, paddingBottom: 34 },
  modalTitle:{ fontSize: 16, fontWeight: '700', color: T.text, marginBottom: 12 },

  stageOption: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: T.border },
  stageDot:    { width: 8, height: 8, borderRadius: 4 },
  stageOptText:{ flex: 1, fontSize: 14, color: T.text, fontWeight: '600' },

  noteInput: { borderWidth: 1, borderColor: T.border, borderRadius: 10, padding: 12, minHeight: 100,
    textAlignVertical: 'top', fontSize: 14, color: T.text },
});